const VENDOR_LEAD_TYPES = new Set([
  "vendor_service_request",
  "vendor_match_request",
  "general_service_request",
  "vendor_specific",
  "multi_vendor_match",
]);

const AGENCY_LEAD_TYPES = new Set([
  "listing_inquiry",
  "schedule_showing",
  "request_info",
  "contact",
]);

export const PARTNER_LEAD_ACTIVE_STATUSES = Object.freeze([
  "accepted",
  "contacted",
  "appointment_set",
  "working",
]);

export const PARTNER_LEAD_CLOSED_STATUSES = Object.freeze([
  "closed_won",
  "closed_lost",
  "rejected",
]);

export function leadDisplayName(lead) {
  return [lead?.first_name, lead?.last_name].filter(Boolean).join(" ") || "";
}

export function parseLeadMetadata(lead) {
  const raw = lead?.metadata_json ?? lead?.metadata;
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

export function isVendorLeadType(leadType) {
  return VENDOR_LEAD_TYPES.has(String(leadType || "").toLowerCase());
}

export function isAgencyLeadType(leadType) {
  const normalized = String(leadType || "").toLowerCase();
  if (AGENCY_LEAD_TYPES.has(normalized)) return true;
  return !isVendorLeadType(normalized) && normalized.length > 0;
}

export function leadCategory(leadType) {
  if (isVendorLeadType(leadType)) return "service";
  if (isAgencyLeadType(leadType)) return "property";
  return "general";
}

export function leadTypeI18nKey(leadType) {
  const normalized = String(leadType || "").toLowerCase();
  if (AGENCY_LEAD_TYPES.has(normalized) || VENDOR_LEAD_TYPES.has(normalized)) {
    return normalized;
  }
  return "other";
}

export function partnerLeadStatusTone(status) {
  switch (status) {
    case "new":
      return "blue";
    case "accepted":
    case "contacted":
    case "appointment_set":
      return "amber";
    case "working":
      return "violet";
    case "closed_won":
      return "green";
    case "closed_lost":
    case "rejected":
      return "slate";
    default:
      return "slate";
  }
}

export function buildLeadSearchHaystack(lead) {
  const meta = parseLeadMetadata(lead);
  return [
    lead?.lead_number,
    leadDisplayName(lead),
    lead?.phone,
    lead?.email,
    lead?.lead_type,
    lead?.listing_title,
    meta?.listingTitle,
    meta?.listing_title,
    meta?.message,
    meta?.casamapaIntent,
    meta?.vendorName,
    meta?.serviceCategory,
    meta?.pageTitle,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export function summarizePartnerLeads(leads = []) {
  const newCount = leads.filter((lead) => lead.partner_status === "new").length;
  const inProgressCount = leads.filter((lead) =>
    PARTNER_LEAD_ACTIVE_STATUSES.includes(lead.partner_status)
  ).length;
  const closedCount = leads.filter((lead) =>
    PARTNER_LEAD_CLOSED_STATUSES.includes(lead.partner_status)
  ).length;
  const needsResponseCount = newCount;

  const propertyCount = leads.filter((lead) => leadCategory(lead.lead_type) === "property").length;
  const serviceCount = leads.filter((lead) => leadCategory(lead.lead_type) === "service").length;

  const pipeline = {
    new: newCount,
    inProgress: inProgressCount,
    closed: closedCount,
  };

  return {
    total: leads.length,
    newCount,
    inProgressCount,
    closedCount,
    needsResponseCount,
    propertyCount,
    serviceCount,
    pipeline,
  };
}

export function whatsappHref(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  return digits ? `https://wa.me/${digits}` : null;
}

export function formatLeadDate(value, locale) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(locale || undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function relativeLeadAge(value, locale) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const diffMs = Date.now() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHours < 1) return "just_now";
  if (diffHours < 24) return "today";
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return "yesterday";
  if (diffDays < 7) return "this_week";
  return formatLeadDate(value, locale);
}
